import { BaseAPI } from 'src/providers/base-api';
import type { RegimeDescriptor } from './regimeModel';

export interface VendorType { id: number; code: string; name: string; category: string; defaultTdsSection: string; portalAccess: boolean; priority: number; active: boolean; sowRequired: boolean; sowEnforcement: string; requiredDocsEnforcement: string; capEnforcement: string; rateEnforcement: string; requiredDocumentKinds: string[]; recurring: boolean; allowedExpenseCategories: string[] | null; defaultPaymentTermsDays: number | null }
export interface Vendor {
  id: number;
  payablesCreated?: boolean;
  name: string;
  legalName: string;
  payablesStatus: string;
  category: string;
  legalConstitution: string;
  pan?: string;
  gstRegistrationType: string;
  gstin?: string;
  gstStateCode?: string;
  placeOfSupply?: string;
  itcEligible: boolean;
  msmeStatus: string;
  udyamNo?: string;
  msmeClass?: string;
  msmeClassifiedOn?: string;
  paymentTermsDays: number;
  riskTier: string;
  country: string;
  currency: string;
  residency: string;
  address?: string;
  contactEmail?: string;
  contactPerson?: string;
  contactPhone?: string;
  types?: VendorType[];
  defaultTdsSection?: string;
}
export interface VendorBank { id: number; version: number; scheme?: string; bankCode?: string; branchCode?: string; bic?: string; accountNumber: string; accountHolder: string; bankName: string; status: string; makerPartyId: number; checkerPartyId?: number }
export interface VendorDocument { id: number; kind: string; title: string; expiresOn?: string; attachmentId: string; workLocationId?: number | null; workLocationName?: string; agreementId?: number | null }

export interface PayablesCountry { iso2: string; name: string }
export async function payablesCountries(): Promise<PayablesCountry[]> {
  const response = await BaseAPI.request('GET', '/v1/geo/countries');
  if (!response || response.status < 200 || response.status >= 300) throw new PayablesRequestError(response?.message || 'Unable to load active countries.', response?.status || 0, response?.data);
  const rows = Array.isArray(response.data) ? response.data : response.data?.data;
  if (!Array.isArray(rows)) throw new Error('Unable to load active countries.');
  return rows;
}

export const REGIMES_KEY = '/regimes';
// The tenant's allowed countries with their tax, identifier and bank contracts.
export interface RegimeCatalog { regimes: RegimeDescriptor[]; defaultPayerCountry?: string }
export async function payablesRegimes(): Promise<RegimeCatalog> {
  const result = await payablesRequest<{ data: RegimeDescriptor[]; defaultPayerCountry?: string }>('GET', REGIMES_KEY);
  return { regimes: Array.isArray(result?.data) ? result.data : [], defaultPayerCountry: result?.defaultPayerCountry || undefined };
}

export class PayablesRequestError extends Error {
  constructor(message: string, public status: number, public data: unknown) {
    super(message);
    this.name = 'PayablesRequestError';
  }
}

// BaseAPI returns error responses rather than rejecting them. Never treat those as saved data.
export async function payablesRequest<T>(method: 'GET' | 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown): Promise<T> {
  const response = await BaseAPI.request(method, `/v1/payables${path}`, body);
  if (!response || response.status < 200 || response.status >= 300) {
    throw new PayablesRequestError(response?.message || 'Payables request failed. Please retry.', response?.status || 0, response?.data);
  }
  return response.data as T;
}

export async function agreementRequest<T>(method: 'GET' | 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown): Promise<T> {
  const response = await BaseAPI.request(method, `/v1/agreements${path}`, body);
  if (!response || response.status < 200 || response.status >= 300) {
    throw new PayablesRequestError(response?.message || 'Unable to load agreement', response?.status || 0, response?.data);
  }
  return response.data as T;
}

export function documentExpiry(expiresOn?: string, now = new Date()): 'No expiry' | 'Expired' | 'Due in 7 days' | 'Due in 30 days' | 'Current' {
  if (!expiresOn) return 'No expiry';
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.ceil((Date.parse(expiresOn.slice(0, 10) + 'T00:00:00Z') - today) / 86400000);
  if (days <= 0) return 'Expired';
  if (days <= 7) return 'Due in 7 days';
  if (days <= 30) return 'Due in 30 days';
  return 'Current';
}
